import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRinging, MapPinLine, Plus, Trash, Envelope, CircleNotch, TelegramLogo } from "@phosphor-icons/react";
import CityCombobox from "@/components/CityCombobox";
import { Link } from "react-router-dom";

export default function AreeSalvate() {
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [checking, setChecking] = useState(false);
  const [tgReady, setTgReady] = useState(false);
  const [form, setForm] = useState({
    name: "", city: "milano", kind: "city", min_yield: 4, min_discount: 30, email_alerts: true, telegram_alerts: false,
  });

  const loadZones = () => api.get("/saved-zones").then((r) => setZones(r.data));
  const loadAlerts = () => api.get("/alerts").then((r) => setAlerts(r.data));

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    loadZones();
    loadAlerts();
    api.get("/settings").then((r) => setTgReady(!!r.data.telegram_enabled && !!r.data.telegram_chat_id)).catch(() => {});
  }, []);

  const createZone = async () => {
    if (!form.name.trim()) { toast.error("Dai un nome alla zona"); return; }
    try {
      await api.post("/saved-zones", form);
      toast.success("Zona salvata");
      setForm({ ...form, name: "" });
      loadZones();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const deleteZone = async (id) => {
    await api.delete(`/saved-zones/${id}`);
    toast.success("Zona rimossa");
    loadZones();
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/alerts/check");
      if (data.new_alerts > 0) toast.success(`${data.new_alerts} nuove occasioni trovate`);
      else toast.info("Nessuna nuova occasione al momento");
      loadAlerts();
      loadZones();
    } finally {
      setChecking(false);
    }
  };

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    loadAlerts();
  };

  const unreadCount = alerts.filter((a) => !a.read).length;

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— AREE MONITORATE</div>
            <h1 className="font-display font-black text-3xl">Zone salvate & Alert</h1>
            <p className="text-sm text-zinc-400 mt-2">Ricevi una notifica quando appaiono nuove aste in aree ad alto rendimento.</p>
          </div>
          <Button onClick={runCheck} disabled={checking} className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold h-11" data-testid="btn-run-check">
            {checking ? <><CircleNotch size={14} weight="bold" className="animate-spin mr-2" />Scansione...</> : <><BellRinging size={14} weight="bold" className="mr-2" />Controlla ora</>}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid lg:grid-cols-3 gap-6">
        {/* Create zone */}
        <div className="border border-white/10 p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <Plus size={16} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">NUOVA ZONA</span>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">NOME</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Milano navigli high-yield" className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid="zone-name" />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
              <div className="mt-1">
                <CityCombobox value={form.city} onChange={(v) => setForm({ ...form, city: v })} cities={cities} testId="zone-city" />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">RENDIMENTO MIN.</Label>
                <span className="font-mono-data text-xs text-lime-400 font-bold">{form.min_yield}%</span>
              </div>
              <Slider value={[form.min_yield]} onValueChange={(v) => setForm({ ...form, min_yield: v[0] })} min={0} max={10} step={0.5} data-testid="zone-min-yield" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">SCONTO ASTA MIN.</Label>
                <span className="font-mono-data text-xs text-amber-400 font-bold">{form.min_discount}%</span>
              </div>
              <Slider value={[form.min_discount]} onValueChange={(v) => setForm({ ...form, min_discount: v[0] })} min={0} max={55} step={5} data-testid="zone-min-discount" />
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-3">
              <div className="flex items-center gap-2">
                <Envelope size={14} weight="bold" className="text-zinc-400" />
                <Label className="font-mono-data text-xs text-zinc-300">Alert email</Label>
              </div>
              <Switch checked={form.email_alerts} onCheckedChange={(v) => setForm({ ...form, email_alerts: v })} data-testid="zone-email-alerts" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TelegramLogo size={14} weight="fill" className="text-[#229ED9]" />
                <Label className="font-mono-data text-xs text-zinc-300">Alert Telegram</Label>
              </div>
              <Switch
                checked={form.telegram_alerts}
                onCheckedChange={(v) => setForm({ ...form, telegram_alerts: v })}
                disabled={!tgReady}
                data-testid="zone-telegram-alerts"
              />
            </div>
            {!tgReady && (
              <Link to="/impostazioni" className="block text-[11px] font-mono-data text-cyan-400 hover:underline -mt-1" data-testid="link-configure-tg">
                → Configura Telegram in Impostazioni per abilitare
              </Link>
            )}
            <Button onClick={createZone} className="w-full rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold" data-testid="btn-save-zone">
              Salva zona
            </Button>
          </div>
        </div>

        {/* Saved zones */}
        <div className="border border-white/10 lg:col-span-2">
          <div className="p-4 border-b border-white/10 bg-zinc-900/50 flex items-center gap-2">
            <MapPinLine size={16} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">LE TUE ZONE ({zones.length})</span>
          </div>
          <div className="divide-y divide-white/5" data-testid="zones-list">
            {zones.length === 0 && (
              <div className="p-8 text-center font-mono-data text-xs text-zinc-500">Nessuna zona salvata. Crea la prima usando il modulo a sinistra.</div>
            )}
            {zones.map((z) => (
              <div key={z.id} className="p-4 flex items-center justify-between" data-testid={`zone-${z.id}`}>
                <div>
                  <div className="font-display font-bold text-base">{z.name}</div>
                  <div className="font-mono-data text-xs text-zinc-500 mt-1">
                    {(cities.find((c) => c.id === z.city) || {}).name || z.city} · rendimento ≥ {z.min_yield}% · sconto ≥ {z.min_discount}%
                    {z.email_alerts && <span className="ml-2 text-cyan-400">· email on</span>}
                    {z.telegram_alerts && <span className="ml-2 text-[#229ED9]">· telegram on</span>}
                  </div>
                </div>
                <button onClick={() => deleteZone(z.id)} className="text-zinc-500 hover:text-red-400 p-2" data-testid={`btn-delete-zone-${z.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>

          {/* Alerts inbox */}
          <div className="p-4 border-t border-white/10 bg-zinc-900/50 flex items-center gap-2">
            <BellRinging size={16} weight="bold" className="text-amber-400" />
            <span className="font-mono-data text-xs text-amber-400 tracking-widest">INBOX ALERT</span>
            {unreadCount > 0 && <Badge className="rounded-none bg-amber-400 text-amber-950 font-mono-data text-[10px] hover:bg-amber-400">{unreadCount} nuovi</Badge>}
          </div>
          <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto" data-testid="alerts-list">
            {alerts.length === 0 && (
              <div className="p-8 text-center font-mono-data text-xs text-zinc-500">Nessun alert. Premi "Controlla ora" per scansionare le tue zone.</div>
            )}
            {alerts.map((a) => (
              <div key={a.id} className={`p-4 flex items-center justify-between ${a.read ? "opacity-50" : ""}`} data-testid={`alert-${a.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="rounded-none bg-amber-400 text-amber-950 font-mono-data text-[10px] font-bold hover:bg-amber-400">-{a.auction_snapshot.discount_pct}%</Badge>
                    <span className="font-display font-bold text-sm">{a.auction_snapshot.title}</span>
                  </div>
                  <div className="font-mono-data text-xs text-zinc-500">
                    Zona: <span className="text-lime-400">{a.zone_name}</span> · Base {formatEuro(a.auction_snapshot.base_price)} · {a.auction_snapshot.auction_date}
                  </div>
                </div>
                {!a.read && (
                  <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => markRead(a.id)} data-testid={`btn-read-${a.id}`}>Segna letto</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
